import { useInboxContext } from '@/contexts/inbox-provider';
import useSessionStorage from '@/hooks/use-session-storage';
import { HOTKEYS } from '@/lib/hotkeys';
import { useParams } from '@tanstack/react-router';
import { isEmpty } from 'lodash';

import { DealScope } from '@/components/all-from-router/_components/deal-filters/filtered-deals/use-filtered-deals.ts';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

type FindInViewFilterContextType = {
  findInViewQuery: string;
  setFindInViewQuery: (query: string) => void;
  showFindInView: boolean;
  setShowFindInView: (show: boolean) => void;
  closeAndReset: () => void;
};

const DefaultFindInViewFilterContext: FindInViewFilterContextType = {
  findInViewQuery: '',
  setFindInViewQuery: () => {},
  showFindInView: false,
  setShowFindInView: () => {},
  closeAndReset: () => {},
};

const FindInViewFilterContext = createContext<FindInViewFilterContextType>(
  DefaultFindInViewFilterContext,
);

export const useFindInViewFilterContext = () => {
  const context = useContext(FindInViewFilterContext);
  if (!context) {
    throw new Error(
      'useFindInViewFilterContext must be used within a FindInViewFilterProvider',
    );
  }
  return context;
};

export const FindInViewFilterProvider = ({
  children,
  scopeKey = DealScope.Active,
}: {
  children: React.ReactNode;
  scopeKey?: DealScope;
}) => {
  const queryStorageKey = `findInViewQuery:${scopeKey}`;
  const showStorageKey = `showFindInView:${scopeKey}`;

  const [findInViewQuery, setFindInViewQuery] = useSessionStorage(
    queryStorageKey,
    '',
  );
  const [showFindInView, setShowFindInView] = useSessionStorage(
    showStorageKey,
    isEmpty(findInViewQuery),
  );

  const { hub: hubPath } = useParams({ strict: false });
  const splitPathnamePrevious = useRef(hubPath?.split('/') ?? []);
  const splitPathname = useMemo(() => hubPath?.split('/') ?? [], [hubPath]);

  useHotkeys(HOTKEYS.FIND_IN_VIEW, (e) => {
    if (!showFindInView) {
      e.preventDefault();
    }
    setShowFindInView(!showFindInView);
  });

  const closeAndReset = useCallback(() => {
    setFindInViewQuery('');
    setShowFindInView(false);
  }, [setFindInViewQuery, setShowFindInView]);

  useEffect(() => {
    // UX NOTE: on mount, if the query is empty, we should close and reset
    if (findInViewQuery === '') {
      closeAndReset();
    }
  }, []);

  const { isInboxView } = useInboxContext();

  useEffect(() => {
    // UX NOTE: Only when the path changes to unrelated path
    // we want to close and reset the filter

    const previousSplitPath = splitPathnamePrevious.current;
    const currentSplitPath = splitPathname;

    const hubHasChanged = previousSplitPath[1] !== currentSplitPath[1];
    if (hubHasChanged) {
      closeAndReset();
    }

    const viewHasChanged = previousSplitPath[2] !== currentSplitPath[2];
    // UX NOTE: the `inbox` and `deal` views are related
    // because `/inbox` shows the initial inbox view,
    // and then when a deal is selected in the inbox view, it'll be a /deal url
    // in this case we want to preserve the find in view query
    // Thus only `closeAndReset` if the view has changed and is not related
    const isGoingFromInboxViewToDealInboxView =
      previousSplitPath[2] === 'inbox' &&
      currentSplitPath[2] === 'deal' &&
      isInboxView;
    const isGoingFromDealInboxViewToInboxView =
      previousSplitPath[2] === 'deal' && currentSplitPath[2] === 'inbox';
    const viewIsRelated =
      isGoingFromInboxViewToDealInboxView ||
      isGoingFromDealInboxViewToInboxView;

    if (viewHasChanged && !viewIsRelated) {
      closeAndReset();
    }

    splitPathnamePrevious.current = splitPathname;
  }, [splitPathname, isInboxView]);

  return (
    <FindInViewFilterContext.Provider
      value={{
        findInViewQuery,
        setFindInViewQuery,
        showFindInView,
        setShowFindInView,
        closeAndReset,
      }}
    >
      {children}
    </FindInViewFilterContext.Provider>
  );
};
